import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Phone, Mail, MessageSquare, Globe, HelpCircle, AlertTriangle, Shield, ShieldAlert, Loader2 } from "lucide-react";
import axios from "axios";
import { useSession } from "@/App";

const API_URL = '';

// Incident type configurations
const INCIDENT_TYPES = [
  { id: "PHONE_CALL", label: "Phone Call", icon: Phone, description: "Someone called you" },
  { id: "EMAIL", label: "Email", icon: Mail, description: "You received an email" },
  { id: "TEXT_MESSAGE", label: "Text Message", icon: MessageSquare, description: "You got a text/SMS" },
  { id: "WEBSITE", label: "Website", icon: Globe, description: "Something on a website" },
  { id: "OTHER", label: "Other", icon: HelpCircle, description: "Something else" },
];

// Risk level configurations
const RISK_CONFIG = {
  CAUTION_SOFT: {
    color: "var(--sidekick-caution)",
    bgColor: "rgba(245, 158, 11, 0.15)",
    borderColor: "rgba(245, 158, 11, 0.3)",
    icon: Shield,
    label: "Be Careful",
  },
  CAUTION_HARD: {
    color: "var(--sidekick-caution)",
    bgColor: "rgba(245, 158, 11, 0.2)",
    borderColor: "rgba(245, 158, 11, 0.4)",
    icon: AlertTriangle,
    label: "Concerning",
  },
  DANGER: {
    color: "var(--sidekick-danger)",
    bgColor: "rgba(239, 68, 68, 0.15)",
    borderColor: "rgba(239, 68, 68, 0.3)",
    icon: ShieldAlert,
    label: "Likely Scam",
  },
};

export default function IncidentPage() {
  const navigate = useNavigate();
  const { currentSessionId } = useSession();
  
  const [step, setStep] = useState(1);
  const [incidentType, setIncidentType] = useState(null);
  const [description, setDescription] = useState("");
  const [senderInfo, setSenderInfo] = useState("");
  const [actionsTaken, setActionsTaken] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    if (!description.trim()) {
      setError("Please describe what happened");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/incident/discuss`, {
        incident_type: incidentType,
        description: description.trim(),
        sender_info: senderInfo.trim(),
        actions_taken: actionsTaken.trim(),
        session_id: currentSessionId,
      });

      setResult(response.data);
      setStep(3);
    } catch (err) {
      console.error("Incident analysis error:", err);
      setError(err.response?.data?.detail || "Failed to analyze. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const riskConfig = result ? RISK_CONFIG[result.risk_level] || RISK_CONFIG.CAUTION_SOFT : null;
  const RiskIcon = riskConfig?.icon || Shield;

  return (
    <div className="incident-page" data-testid="incident-page">
      {/* Header */}
      <header className="incident-header">
        <button 
          className="back-button" 
          onClick={() => step === 1 ? navigate("/home") : setStep(step - 1)}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </button>
        <h1>Incident Discussion</h1>
      </header>

      {/* Step 1: Select Type */}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="incident-step"
          >
            <h2>What happened?</h2>
            <p className="step-description">Select the type of communication you're concerned about</p>
            
            <div className="type-grid">
              {INCIDENT_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    className={`type-card ${incidentType === type.id ? "selected" : ""}`}
                    onClick={() => {
                      setIncidentType(type.id);
                      setStep(2);
                    }}
                    data-testid={`type-${type.id.toLowerCase()}`}
                  >
                    <Icon size={28} />
                    <span className="type-label">{type.label}</span>
                    <span className="type-desc">{type.description}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="incident-step"
          >
            <h2>Tell me more</h2>
            <p className="step-description">
              Describe what happened so I can help you understand if it might be a scam
            </p>

            <div className="details-form">
              <div className="form-group">
                <label>What did they say or do?</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what happened... For example: They said I won a prize and needed to pay shipping, or they claimed to be from the IRS and said I owed money..."
                  rows={5}
                  disabled={loading}
                  data-testid="description-input"
                />
              </div>

              <div className="form-group">
                <label>Who contacted you? (optional)</label>
                <input
                  type="text"
                  value={senderInfo}
                  onChange={(e) => setSenderInfo(e.target.value)}
                  placeholder="Phone number, email address, company name..."
                  disabled={loading}
                  data-testid="sender-input"
                />
              </div>

              <div className="form-group">
                <label>Did you do anything yet? (optional)</label>
                <textarea
                  value={actionsTaken}
                  onChange={(e) => setActionsTaken(e.target.value)}
                  placeholder="Did you click a link, give them information, or send money?"
                  rows={2}
                  disabled={loading}
                  data-testid="actions-input"
                />
              </div>

              {error && (
                <div className="error-message" data-testid="error-message">
                  <AlertTriangle size={18} />
                  {error}
                </div>
              )}

              <button
                className="analyze-button"
                onClick={handleAnalyze}
                disabled={loading || !description.trim()}
                data-testid="analyze-button"
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Shield size={20} />
                    Analyze This
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Results */}
        {step === 3 && result && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="incident-step"
          >
            {/* Risk Assessment Card */}
            <div 
              className="assessment-card"
              style={{
                backgroundColor: riskConfig.bgColor,
                borderColor: riskConfig.borderColor,
              }}
            >
              <div className="assessment-header">
                <RiskIcon 
                  size={36} 
                  style={{ color: riskConfig.color }} 
                />
                <div className="assessment-info">
                  <span 
                    className="assessment-label"
                    style={{ color: riskConfig.color }}
                  >
                    {riskConfig.label}
                  </span>
                  <span className="assessment-score">
                    Risk Score: {result.score}/100
                  </span>
                </div>
              </div>
              <p className="assessment-text">{result.assessment}</p>
            </div>

            {/* Red Flags */}
            {result.red_flags && result.red_flags.length > 0 && (
              <div className="section-card">
                <h3>Warning Signs Found</h3>
                <ul className="flag-list">
                  {result.red_flags.map((flag, idx) => (
                    <li key={idx} className="flag-item">
                      <AlertTriangle size={16} />
                      {flag}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommended Actions */}
            <div className="section-card">
              <h3>What You Should Do</h3>
              <ul className="action-list">
                {result.recommended_actions.map((action, idx) => (
                  <li key={idx}>
                    <span className="action-num">{idx + 1}</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>

            {/* Follow-up Questions */}
            {result.follow_up_questions && result.follow_up_questions.length > 0 && (
              <div className="section-card">
                <h3>Questions to Consider</h3>
                <ul className="question-list">
                  {result.follow_up_questions.map((q, idx) => (
                    <li key={idx}>{q}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="result-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setStep(1);
                  setIncidentType(null);
                  setDescription("");
                  setSenderInfo("");
                  setActionsTaken("");
                  setResult(null);
                }}
              >
                Report Another Incident
              </button>
              <button
                className="btn-primary"
                onClick={() => navigate("/home")}
              >
                Back to Home
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .incident-page {
          min-height: 100vh;
          background: var(--sidekick-bg);
          padding: 0 20px 40px;
        }

        .incident-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px 0;
          position: sticky;
          top: 0;
          background: var(--sidekick-bg);
          z-index: 10;
        }

        .back-button {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          border: 1px solid var(--sidekick-border);
          background: var(--sidekick-card);
          color: var(--sidekick-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .back-button:hover {
          background: var(--sidekick-hover);
          border-color: var(--sidekick-primary);
        }

        .incident-header h1 {
          font-size: 24px;
          font-weight: 700;
          color: var(--sidekick-text);
          margin: 0;
        }

        .incident-step h2 {
          font-size: 22px;
          font-weight: 700;
          color: var(--sidekick-text);
          margin: 0 0 8px;
        }

        .step-description {
          font-size: 15px;
          color: var(--sidekick-muted);
          margin: 0 0 24px;
        }

        .type-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .type-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 24px 16px;
          background: var(--sidekick-card);
          border: 2px solid var(--sidekick-border);
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.2s;
          color: var(--sidekick-text);
        }

        .type-card:hover {
          border-color: var(--sidekick-primary);
          background: var(--sidekick-hover);
        }

        .type-card.selected {
          border-color: var(--sidekick-primary);
          background: rgba(59, 130, 246, 0.1);
        }

        .type-label {
          font-size: 16px;
          font-weight: 600;
        }

        .type-desc {
          font-size: 12px;
          color: var(--sidekick-muted);
          text-align: center;
        }

        .details-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 600;
          color: var(--sidekick-text);
        }

        .form-group input,
        .form-group textarea {
          padding: 14px;
          border: 1px solid var(--sidekick-border);
          border-radius: 12px;
          background: var(--sidekick-card);
          color: var(--sidekick-text);
          font-size: 16px;
          font-family: inherit;
          transition: all 0.2s;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--sidekick-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }

        .form-group textarea {
          resize: vertical;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 12px;
          color: var(--sidekick-danger);
          font-size: 14px;
        }

        .analyze-button {
          padding: 16px 24px;
          border: none;
          border-radius: 12px;
          background: var(--sidekick-primary);
          color: white;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.2s;
        }

        .analyze-button:hover:not(:disabled) {
          background: var(--sidekick-primary-hover);
          transform: translateY(-1px);
        }

        .analyze-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .assessment-card {
          padding: 24px;
          border-radius: 16px;
          border: 2px solid;
          margin-bottom: 20px;
        }

        .assessment-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }

        .assessment-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .assessment-label {
          font-size: 20px;
          font-weight: 700;
        }

        .assessment-score {
          font-size: 14px;
          color: var(--sidekick-muted);
        }

        .assessment-text {
          font-size: 16px;
          line-height: 1.6;
          color: var(--sidekick-text);
          margin: 0;
        }

        .section-card {
          background: var(--sidekick-card);
          border: 1px solid var(--sidekick-border);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 16px;
        }

        .section-card h3 {
          font-size: 16px;
          font-weight: 700;
          color: var(--sidekick-text);
          margin: 0 0 16px;
        }

        .flag-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .flag-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          font-size: 14px;
          color: var(--sidekick-danger);
          padding: 10px 14px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 10px;
        }

        .action-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .action-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          font-size: 15px;
          color: var(--sidekick-text);
        }

        .action-num {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--sidekick-primary);
          color: white;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .question-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .question-list li {
          font-size: 14px;
          color: var(--sidekick-text);
          padding: 12px 14px;
          background: rgba(59, 130, 246, 0.1);
          border-radius: 10px;
        }

        .result-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .btn-secondary {
          flex: 1;
          padding: 14px;
          border: 1px solid var(--sidekick-border);
          border-radius: 12px;
          background: var(--sidekick-card);
          color: var(--sidekick-text);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          border-color: var(--sidekick-primary);
          background: var(--sidekick-hover);
        }

        .btn-primary {
          flex: 1;
          padding: 14px;
          border: none;
          border-radius: 12px;
          background: var(--sidekick-primary);
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary:hover {
          background: var(--sidekick-primary-hover);
        }
      `}</style>
    </div>
  );
}
